
import React from "react";
import { Capability, Schemes, Utils } from "../../../../../utils";
import { i18nManager } from "../../../../../i18n";
import { Elements } from "../../../../shared/Elements";
import { Button, ButtonManageRef, Classname, Dropdown, FormControl, Scheme, TextInput } from "@rivtn/noseur";
import { BaseService, IMember, ITeam, ITeamMember, SanitizedResponse, TeamRole, TeamService } from "../../../../../services";
import { ConfirmDialog, Siren } from "../../../../shared/ConfirmDialog";
import { usePlan } from "@syllektis/oya-gbewa-react";
import UpSellPlan from "../../../../shared/UpSellPlan";

export interface ModeratorsProps {
    team: ITeam;
    profile: IMember;
}

function Moderators(props: ModeratorsProps) {

    const { team, profile } = props;
    const teamService = TeamService.getInstance();
    const labels = i18nManager.Labels.teams.settings.general.moderators;
    const [isTeamOwner, setIsTeamOwner] = React.useState<boolean>(false);
    const [teamMembers, setTeamMembers] = React.useState<ITeamMember[]>();
    const { maximumTeamModerator } = usePlan({ maximumTeamModerator: 1 });
    const [teamModerators, setTeamModerators] = React.useState<ITeamMember[]>();

    React.useEffect(fetchInformation, []);

    return (<div style={{ marginTop: 10, display: "flex", flexDirection: "column" }}>
        <UpSellPlan type="icon" capability={Capability.TEAM_OWNERSHIP_TRANSFER} childrenRelayProps={{ style: { opacity: 0.7 } }} >
            <span className={Classname.build("link danger", { "noseur-disabled": !isTeamOwner })} style={{ margin: "5px 0px" }} onClick={transferOwnership}>{labels.transfer_ownership}</span>
        </UpSellPlan>

        <UpSellPlan capability={Capability.MAXIMUM_TEAM_MODERATOR} currentValue={teamModerators?.length} style={{ marginTop: 15 }}  >
            <FormControl isFieldset fill scheme={Schemes.OMNEHOST} label={labels.search_and_select_member}>
                <Dropdown fill disabled={teamModerators && teamModerators?.length >= maximumTeamModerator} options={teamMembers?.map((tm) => {
                    if (teamModerators?.find((t) => t.external_id === tm.external_id)) return { ...tm, not_selectable: true } as any;
                    return tm;
                })} optionMap={{
                    label: "display_name",
                    icon: "passport_picture_url",
                }} onSelectOption={(o: ITeamMember) => {
                    setTeamModerators([...(teamModerators ?? []), o]);
                }} />
            </FormControl>
        </UpSellPlan>
        <div className="team-mods">
            {!teamModerators?.length && (Elements.LOADING_STATE)}
            {teamModerators?.map((teamModerator) => {
                const isOwner = teamModerator.role === TeamRole.OWNER;
                const isMember = teamModerator.role === TeamRole.MEMBER;
                const isYou = teamModerator.member?.external_id === profile.external_id && (i18nManager.Labels.host.analytics.earnings.you);

                return (<div className="team-mod" key={teamModerator.external_id}>
                    <i style={{ marginTop: 10 }} className={Classname.build("fa fa-times link danger", { "noseur-disabled": isOwner })} onClick={() => removeTeamModerator(teamModerator)} />
                    <img alt={teamModerator.display_name} src={teamModerator.passport_picture_url} />
                    <div className="details">
                        <span className="n">
                            {teamModerator.display_name}{" "}
                            {(isYou || isOwner) && `(${isOwner ? i18nManager.Labels.host.analytics.earnings.owner + (isYou ? ", " : "") : ""}${isYou ? i18nManager.Labels.host.analytics.earnings.you : ""})`}
                        </span>
                        <span className="e">{teamModerator.member?.user_detail?.email}</span>
                        <span className="m">{labels.moderating_since} {isMember ? "" : Utils.formatToFineDate(teamModerator.join_date ?? "", true, true)}</span>
                    </div>
                </div>);
            })}
        </div>
        <UpSellPlan capability={Capability.MANAGE_TEAM_MODERATOR} style={{ marginTop: 30, padding: "10px 25px", alignSelf: "flex-end" }} childrenRelayProps={Elements.BUTTON_UPGRADE_PROPS} >
            <Button scheme={Schemes.OMNEHOST} loadingProps={Elements.BUTTON_LOADING_PROPS} text={labels.update_moderators} onClick={(e) => addNewModerators(e.manageRef)} />
        </UpSellPlan>
    </div>);

    function fetchInformation() {
        teamService.queryTeamMembers(team.external_id, { status: "ACTIVE" }).then(({ content }: SanitizedResponse<ITeamMember>) => {
            setTeamMembers(content);
            setTeamModerators(content.filter((m) => m.role === TeamRole.OWNER || m.role === TeamRole.MODERATOR));
            for (const member of content) {
                if (member.role === TeamRole.OWNER && member.member?.external_id === profile.external_id) {
                    setIsTeamOwner(true);
                    break;
                }
            }
        }).catch(BaseService.reportError);
    }

    async function addNewModerators(manageRef?: ButtonManageRef) {
        manageRef?.setLoadingState(true);
        try {
            for (const newModerator of (teamModerators?.filter((t) => t.role === TeamRole.MEMBER) ?? [])) {
                await teamService.addModerator(newModerator.external_id);
            }
            fetchInformation();
            Siren.alert(labels.moderator_updated_successfully);
        } catch (error: any) {
            BaseService.reportError(error);
        }
        manageRef?.setLoadingState(false);
    }

    function transferOwnership() {
        let teamName = "";
        let teamModeratorId: string = "";
        const dialogLabels = labels.dialog.transfer;
        const dialogErrorRef = { current: null } as any;

        const dialog = ConfirmDialog({
            closable: true,
            title: dialogLabels.title,
            confirmScheme: Scheme.DANGER,
            confirmLabel: dialogLabels.title,
            desc: dialogLabels.desc.replace("${team_name}", team.name),
            content: (<div className="dialog-content" style={{ marginTop: 10 }}>
                <FormControl isFieldset fill scheme={Schemes.OMNEHOST} style={{ marginTop: 15 }} label={labels.search_and_select_member}>
                    <Dropdown fill options={teamMembers} optionMap={{
                        label: "display_name",
                        icon: "passport_picture_url",
                    }} onSelectOption={(o: ITeamMember) => {
                        teamModeratorId = o.external_id;
                    }} />
                </FormControl>
                <FormControl isFieldset fill label={dialogLabels.team_name} style={{ marginTop: 15 }} scheme={Schemes.OMNEHOST}>
                    <TextInput onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        teamName = v;
                    }} />
                </FormControl>
                <div ref={dialogErrorRef} className="error" style={{ marginBottom: 10 }}> </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                if (teamName !== team.name) {
                    dialogErrorRef.current!.innerText = i18nManager.Labels.teams.dialog.confirm_team.name_does_not_match;
                    return false;
                }
                button?.setLoadingState(true);
                teamService.transferOwnership(teamModeratorId).then(({ }: SanitizedResponse<any>) => {
                    dialog.hide(() => {
                        fetchInformation();
                        Siren.alert(dialogLabels.transfer_successful);
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function removeTeamModerator(teamModerator: ITeamMember) {
        let teamName = "", memberName = "";
        const dialogLabels = labels.dialog.remove;
        const dialogErrorRef = { current: null } as any;

        const dialog = ConfirmDialog({
            closable: true,
            title: dialogLabels.title,
            confirmScheme: Scheme.DANGER,
            confirmLabel: dialogLabels.demote_moderator,
            desc: dialogLabels.desc.replace("${team_name}", team.name).replace("${member}", teamModerator.display_name),
            content: (<div className="dialog-content" style={{ marginTop: 10 }}>
                <FormControl isFieldset fill label={dialogLabels.enter.replaceAll("${member}", teamModerator.display_name)} style={{ marginTop: 15 }} scheme={Schemes.OMNEHOST}>
                    <TextInput onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        memberName = v;
                    }} />
                </FormControl>
                <FormControl isFieldset fill label={dialogLabels.team_name} style={{ marginTop: 15 }} scheme={Schemes.OMNEHOST}>
                    <TextInput onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        teamName = v;
                    }} />
                </FormControl>
                <div ref={dialogErrorRef} className="error" style={{ marginBottom: 10 }}> </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                if (memberName !== teamModerator.display_name) {
                    dialogErrorRef.current!.innerText = i18nManager.Labels.teams.dialog.confirm_team.mem_name_does_not_match;
                    return false;
                }
                if (teamName !== team.name) {
                    dialogErrorRef.current!.innerText = i18nManager.Labels.teams.dialog.confirm_team.name_does_not_match;
                    return false;
                }
                button?.setLoadingState(true);
                teamService.removeModerator(teamModerator.external_id).then(({ }: SanitizedResponse<any>) => {
                    dialog.hide(() => {
                        fetchInformation();
                        Siren.alert(dialogLabels.remove_successful);
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

}

export default Moderators;
